import React, { useEffect, useState } from "react";
import { Key, Smartphone, CreditCard, Clipboard, AlertTriangle, CheckCircle, Loader2, Shield } from "lucide-react";

const MFAManagement = () => {
  const [mfaData, setMfaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('authenticator_app');

  useEffect(() => {
    const fetchMfaData = async () => {
      try {
        const response = await fetch(`/api/auth/howto`);
        if (!response.ok) {
          throw new Error("Failed to fetch MFA data");
        }
        const data = await response.json();
        setMfaData(data.mfa_manual);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMfaData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 style={{ width: '48px', height: '48px', color: 'var(--cares-blue)', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          background: 'color-mix(in srgb, var(--cares-orange) 22%, var(--cares-surface-card))',
          border: '1px solid var(--cares-orange)',
          borderLeft: '4px solid var(--cares-orange)',
          borderRadius: '8px',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <AlertTriangle style={{ width: '24px', height: '24px', color: 'var(--cares-orange)', flexShrink: 0 }} />
          <div>
            <p style={{ fontSize: '14px', color: 'var(--cares-orange)', margin: 0, fontWeight: '500' }}>
              Error loading MFA information: {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const renderMethod = (methodKey) => {
    const method = mfaData[methodKey];
    return (
      <div>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--cares-text-1)', marginBottom: '16px' }}>
          {method.title}
        </h3>
        <ol style={{ paddingLeft: '24px', margin: 0, listStyleType: 'decimal' }}>
          {method.steps.map((step, index) => (
            <li key={index} style={{
              fontSize: '14px',
              color: 'var(--cares-text-3)',
              marginBottom: '12px',
              lineHeight: '1.6'
            }}>
              <span style={{ color: 'var(--cares-text-1)', fontWeight: '500' }}>{step}</span>
            </li>
          ))}
        </ol>
        {method.note && (
          <div style={{
            background: 'color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))',
            borderLeft: '4px solid var(--cares-amber)',
            borderRadius: '0 8px 8px 0',
            padding: '16px',
            marginTop: '20px',
            display: 'flex',
            gap: '12px'
          }}>
            <AlertTriangle style={{ width: '20px', height: '20px', color: 'var(--cares-amber)', flexShrink: 0 }} />
            <p style={{ fontSize: '14px', color: 'var(--cares-amber)', margin: 0, lineHeight: '1.6' }}>
              {method.note}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Page Header - Title removed to avoid duplication with Dashboard Header */}

      {/* Tabs and Content */}
      <div style={{
        background: 'var(--cares-surface-card)',
        borderRadius: '12px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{ borderBottom: '1px solid var(--cares-line-card)' }}>
          <div style={{ display: 'flex' }}>
            <button
              onClick={() => setActiveTab('authenticator_app')}
              style={{
                padding: '16px 24px',
                borderBottom: activeTab === 'authenticator_app' ? '2px solid var(--cares-blue)' : '2px solid transparent',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'authenticator_app' ? 'var(--cares-blue)' : 'var(--cares-text-label)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { if (activeTab !== 'authenticator_app') e.currentTarget.style.color = 'var(--cares-text-3)'; }}
              onMouseOut={(e) => { if (activeTab !== 'authenticator_app') e.currentTarget.style.color = 'var(--cares-text-label)'; }}
            >
              <Smartphone style={{ width: '18px', height: '18px' }} />
              Authenticator App
            </button>
            <button
              onClick={() => setActiveTab('smart_card')}
              style={{
                padding: '16px 24px',
                borderBottom: activeTab === 'smart_card' ? '2px solid var(--cares-blue)' : '2px solid transparent',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                color: activeTab === 'smart_card' ? 'var(--cares-blue)' : 'var(--cares-text-label)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => { if (activeTab !== 'smart_card') e.currentTarget.style.color = 'var(--cares-text-3)'; }}
              onMouseOut={(e) => { if (activeTab !== 'smart_card') e.currentTarget.style.color = 'var(--cares-text-label)'; }}
            >
              <CreditCard style={{ width: '18px', height: '18px' }} />
              Smart Card
            </button>
          </div>
        </div>
        <div style={{ padding: '32px' }}>
          {mfaData && renderMethod(activeTab)}
        </div>
      </div>

      {/* Best Practices */}
      <div style={{
        background: 'color-mix(in srgb, var(--cares-blue) 14%, var(--cares-surface-card))',
        borderLeft: '4px solid var(--cares-blue)',
        borderRadius: '0 8px 8px 0',
        padding: '20px',
        marginTop: '24px'
      }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <CheckCircle style={{ width: '20px', height: '20px', color: 'var(--cares-blue)', flexShrink: 0, marginTop: '2px' }} />
          <div>
            <h3 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--cares-blue)', margin: '0 0 12px 0' }}>
              MFA Best Practices
            </h3>
            <ul style={{ margin: 0, paddingLeft: '20px', listStyleType: 'disc' }}>
              <li style={{ fontSize: '14px', color: 'var(--cares-blue)', marginBottom: '6px' }}>
                Always keep your backup codes in a secure location
              </li>
              <li style={{ fontSize: '14px', color: 'var(--cares-blue)', marginBottom: '6px' }}>
                Set up at least two authentication methods
              </li>
              <li style={{ fontSize: '14px', color: 'var(--cares-blue)' }}>
                Update your recovery options regularly
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MFAManagement;